/**
 * Activates export-only canvas styles and restores the element's prior state.
 * Cleanup is safe to call more than once because export handlers invoke it
 * both after capture and from their error/finally path.
 */
const isDarkBackground = (color?: string): boolean => {
    if (color) {
        const value = color.trim();
        const hex = value.match(/^#([\da-f]{3}|[\da-f]{6})$/i)?.[1];
        if (hex) {
            const normalized = hex.length === 3
                ? hex.split('').map((part) => part + part).join('')
                : hex;
            const red = parseInt(normalized.slice(0, 2), 16);
            const green = parseInt(normalized.slice(2, 4), 16);
            const blue = parseInt(normalized.slice(4, 6), 16);
            return red * 0.299 + green * 0.587 + blue * 0.114 < 150;
        }
    }

    return document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark');
};

export const beginVisualExport = (
    element: HTMLElement,
    backgroundColor?: string
): (() => void) => {
    const wasAlreadyExporting = element.getAttribute('data-exporting') === 'true';
    const previousVariables = {
        background: element.style.getPropertyValue('--flowi-export-connector-label-bg'),
        border: element.style.getPropertyValue('--flowi-export-connector-label-border'),
        text: element.style.getPropertyValue('--flowi-export-connector-label-text')
    };
    const dark = isDarkBackground(backgroundColor);

    element.setAttribute('data-exporting', 'true');
    element.style.setProperty('--flowi-export-connector-label-bg', dark ? '#0f172a' : '#ffffff');
    element.style.setProperty('--flowi-export-connector-label-border', dark ? '#334155' : '#e2e8f0');
    element.style.setProperty('--flowi-export-connector-label-text', dark ? '#f8fafc' : '#111827');

    return () => {
        if (!wasAlreadyExporting) element.removeAttribute('data-exporting');
        Object.entries(previousVariables).forEach(([name, value]) => {
            const property = `--flowi-export-connector-label-${name === 'background' ? 'bg' : name}`;
            if (value) element.style.setProperty(property, value);
            else element.style.removeProperty(property);
        });
    };
};
